import { Component, DOCUMENT, HostListener, Inject, Input, NgZone, OnChanges, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';
import { DIAL_LOGS_TOOLTIP_OPTIONS } from './dial-logs-timeline.tooltip';
import { DialLogsFailureDetailComponent } from './dial-logs-failure-detail.component';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { EVENT_FIELDS, EventFields } from '@netfoundry-ui/shared/model';
import { ApiService, LoggerService } from '@netfoundry-ui/shared/services';
import { DialLogsTimelineDrawerService } from './dial-logs-timeline-drawer.service';
import { ElasticsearchService } from '@netfoundry-ui/shared/elasticsearch';
import { EdgeRouterServiceV2, TEMPLATE_SEARCH_SERVICE, TemplateSearchService } from '@netfoundry-ui/shared/apiv2';
import {
    DialLogEventFields,
    DialLogGroup,
    DialLogRecord,
    DialSignatureGroup,
    GroupSortColumn,
    GroupSortDir,
    DialTimeBucket,
    buildAreaPath,
    buildLinePath,
    buildSignatureGroups,
    buildTimeBuckets,
    collapseNames,
    niceMax,
    decorateRecord,
    dialGroupKey,
    dialGroupLabel,
    displayEntityName,
    toDialLogRecords,
} from './dial-logs-timeline.model';

/** One bucket's hover hit-zone + focus geometry, in SVG 0–100 x / 0–H y space. */
interface DialChartPoint {
    xPct: number;
    widthPct: number;
    successYPct: number;
    failedYPct: number;
    windowLabel: string;
    success: number;
    failed: number;
    total: number;
}

interface DialLogsTabData {
    resourceType?: 'endpoint' | 'service' | string;
    model?: { id?: string; name?: string };
    networkId?: string;
    networkGroupId?: string;
    /** Initial time range to pre-select on first open (mirrors the opener's own range). */
    timeRange?: string;
}

interface RangeOption {
    id: '1h' | '24h' | '7d';
    label: string;
    /** Fuller name for the on-chart range label (e.g. "Last 24 hours"). */
    rangeLabel: string;
    amount: number;
    unit: moment.unitOfTime.DurationConstructor;
}

interface StatusOption {
    id: 'all' | 'success' | 'failed';
    label: string;
}

interface DirectionOption {
    id: 'dialed' | 'hosted';
    label: string;
}

/** One binding-identity sub-group inside an expanded "Multiple" group. */
interface BinderSubgroup {
    id: string;
    name: string | null;
    link: unknown[] | null;
    records: DialLogRecord[];
    count: number;
    failedCount: number;
    latestTimestampMs: number;
}

@Component({
    selector: 'app-dial-logs-timeline-tab',
    templateUrl: './dial-logs-timeline-tab.component.html',
    styleUrls: ['./dial-logs-timeline-tab.component.scss'],
    providers: [{ provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: DIAL_LOGS_TOOLTIP_OPTIONS }],
    standalone: false,
})
export class DialLogsTimelineTabComponent implements OnInit, OnChanges, OnDestroy {
    @Input() data: DialLogsTabData | undefined;
    @Input() networkId: string | undefined;
    @Input() organizationId: string | undefined;

    readonly auditRanges: RangeOption[] = [
        { id: '1h', label: '1h', rangeLabel: 'Last hour', amount: 1, unit: 'hours' },
        { id: '24h', label: '24h', rangeLabel: 'Last 24 hours', amount: 24, unit: 'hours' },
        { id: '7d', label: '7d', rangeLabel: 'Last 7 days', amount: 7, unit: 'days' },
    ];
    readonly statusFilters: StatusOption[] = [
        { id: 'all', label: 'All' },
        { id: 'success', label: 'Success' },
        { id: 'failed', label: 'Failed' },
    ];
    /** Direction toggle (endpoint view only): services this connector dials vs hosts. */
    readonly directionFilters: DirectionOption[] = [
        { id: 'dialed', label: 'Dialed' },
        { id: 'hosted', label: 'Hosted' },
    ];
    /** Auto-refresh interval options (id = seconds; 0 = off). */
    readonly autoRefreshOptions: { id: number; label: string }[] = [
        { id: 0, label: 'Off' },
        { id: 15, label: '15s' },
        { id: 30, label: '30s' },
        { id: 60, label: '1m' },
        { id: 300, label: '5m' },
    ];
    readonly GROUP_PAGE_SIZE = 25;
    readonly CHILD_PAGE_SIZE = 25;
    /**
     * Per-query record cap (ES max_result_window). Larger than the template's
     * default 500 so the table and activity chart cover more of the selected
     * range. Note: on very busy connectors a long range can still exceed this —
     * an accurate full-range time-series needs a server-side date_histogram.
     */
    private static readonly FETCH_SIZE = '10000';

    isEndpointView = false;
    endpointId = '';
    endpointName = '';
    serviceId = '';
    serviceName = '';

    records: DialLogRecord[] = [];
    groups: DialLogGroup[] = [];

    // Activity chart (client-side buckets of the same filtered set the table shows).
    readonly CHART_VIEW_W = 1000;
    readonly CHART_VIEW_H = 100;

    // User-resizable plot height (px), dragged via the divider under the chart and persisted.
    private static readonly CHART_MIN_REM = 4.5;
    private static readonly CHART_MAX_REM = 18;
    private static readonly CHART_DEFAULT_REM = 6;
    private static readonly CHART_H_KEY = 'dlt.chartHeightPx';
    chartBodyHeightPx = 0;
    resizingChart = false;
    private chartMinPx = 0;
    private chartMaxPx = 0;
    private chartResizeStartY = 0;
    private chartResizeStartH = 0;
    private readonly chartMoveHandler = (e: MouseEvent) => this.onChartResizeMove(e);
    private readonly chartUpHandler = () => this.endChartResize();

    chartMax = 0;
    chartSuccessLine = '';
    chartSuccessArea = '';
    chartFailedLine = '';
    chartFailedArea = '';
    chartPoints: DialChartPoint[] = [];
    chartXTicks: string[] = [];
    chartTotalDials = 0;
    chartFailedCount = 0;
    showSuccess = true;
    showFailed = true;
    hoveredBucket: number | null = null;

    selectedRange: RangeOption['id'] = '24h';
    statusFilter: StatusOption['id'] = 'all';
    directionFilter: DirectionOption['id'] = 'dialed';
    autoRefreshSeconds = 0;
    autoRefreshMenuOpen = false;
    search = '';
    groupSortCol: GroupSortColumn = 'lastDial';
    groupSortDir: GroupSortDir = 'desc';

    isLoading = true;
    initialized = false;
    errorMessage: string | undefined;
    // Placeholder rows rendered while the first fetch is in flight.
    readonly skeletonRows = Array.from({ length: 7 });

    // Parent-table column widths (%) — draggable via header resize handles.
    colWidths: number[] = [26, 24, 20, 16, 14];
    private static readonly MIN_COL_PCT = 8;
    private colResizeIndex = -1;
    private colResizeStartX = 0;
    private colResizeTableWidth = 0;
    private colResizePairTotal = 0;
    private colResizeStartWidth = 0;
    private readonly colResizeMove = (e: MouseEvent) => this.onColResizeMove(e);
    private readonly colResizeUp = () => this.endColResize();

    // Detail (child) column widths (%) — keyed so they stay stable across the
    // conditional binder column and the target/error/time hide toggles.
    detailColWidths: Record<string, number> = { event: 22, binder: 26, target: 34, error: 18, dials: 12, time: 14 };
    private detailResizeKey: string | null = null;
    private detailResizeNextKey: string | null = null;
    private detailResizeStartX = 0;
    private detailResizeTableWidth = 0;
    private detailResizeStartWidth = 0;
    private detailResizePairTotal = 0;
    private readonly detailResizeMove = (e: MouseEvent) => this.onDetailColResizeMove(e);
    private readonly detailResizeUp = () => this.endDetailColResize();

    // Parent-column visibility (the primary/name column is always shown).
    colVisible: Record<string, boolean> = { counterpart: true, addr: true, dials: true, time: true };
    // Expanded-detail column visibility (the Event column is always shown).
    detailColVisible: Record<string, boolean> = { target: true, error: true, time: true };
    columnsMenuOpen = false;

    // Identity filters (multi-select), populated from the loaded dials.
    dialerFilter = new Set<string>();
    binderFilter = new Set<string>();
    dialerMenuOpen = false;
    binderMenuOpen = false;
    dialerSearch = '';
    binderSearch = '';

    private static readonly DEFAULT_COL_VISIBLE = { counterpart: true, addr: true, dials: true, time: true };
    private static readonly DEFAULT_DETAIL_COL_VISIBLE = { target: true, error: true, time: true };

    groupLimit = this.GROUP_PAGE_SIZE;
    childLimits: Record<string, number> = {};

    newStartTime: moment.Moment = moment().subtract(24, 'hours');
    newEndTime: moment.Moment = moment();

    private expandedKeys = new Set<string>();
    /** Key of the group we auto-expanded (single-group result), to avoid re-expanding after a manual collapse. */
    private autoExpandedKey?: string;
    private subscription = new Subscription();
    private fetchSub?: Subscription;
    private chartAggSub?: Subscription;
    /** Buckets currently driving the chart — the server aggregation (full history) once it lands,
     * else the capped client-side set. Kept so series toggles re-render without another fetch. */
    private chartBuckets: DialTimeBucket[] = [];
    /** True once the full-history aggregation has rendered for the current fetch, so the (capped)
     * client render doesn't clobber it — whichever of the two responses lands second. Reset per fetch. */
    private chartFromAgg = false;
    /** Oldest timestamp the (row-capped) table lists, or null when the table isn't truncated. */
    private tableCutoffTs: number | null = null;
    /** A "load older" fetch is in flight. */
    loadingMore = false;
    private loadMoreSub?: Subscription;
    private autoRefreshHandle?: ReturnType<typeof setInterval>;
    private zitiSheet?: CSSStyleSheet;

    // ---- Drag-to-select a custom range (brush) ------------------------------
    // A press-drag across the plot picks a sub-range and applies it as a custom window, mirroring the
    // Data Usage chart. Fractions/percents (not viewBox px) since this plot is laid out in %.
    brushing = false;
    brushLeftPct = 0;
    brushWidthPct = 0;
    brushTipLeftPct = 0;
    brushLabel = '';
    /** A custom window (brushed or picked) is in effect — deselects the 24h/7d/30d presets. */
    customRange = false;
    /** Whether the Custom-tab calendar popover is open. */
    showCustom = false;
    private brushStartFrac = 0;
    private brushRect?: DOMRect;
    private static readonly BRUSH_MIN_PX = 8; // travel below this is treated as a click, not a drag
    private static readonly BRUSH_MIN_MS = 60_000; // and a selection must span at least a minute
    // Bound outside the zone for the drag's duration so idle mouse moves don't churn change detection.
    private readonly onBrushMove = (ev: MouseEvent) => this.zone.run(() => this.updateBrush(ev));
    private readonly onBrushUp = (ev: MouseEvent) => this.zone.run(() => this.endBrush(ev));

    constructor(
        private elasticsearch: ElasticsearchService,
        @Inject(TEMPLATE_SEARCH_SERVICE) public templateService: TemplateSearchService,
        @Inject(EVENT_FIELDS) private eventFields: EventFields,
        public apiService: ApiService,
        private logger: LoggerService,
        private drawerService: DialLogsTimelineDrawerService,
        private renderer: Renderer2,
        private dialog: MatDialog,
        private edgeRouterService: EdgeRouterServiceV2,
        private zone: NgZone,
        @Inject(DOCUMENT) private document: Document
    ) {}

    /** zitiId → {name, id} for resolving the router path in the failure modal. */
    private routerNameMap = new Map<string, { name: string; id: string }>();
    private routerMapLoaded = false;

    ngOnInit(): void {
        this.initChartHeight();
        this.applyData(true);
        this.initialized = true;
        // The network resolves asynchronously and may not be ready on `data` yet,
        // so drive the (first and subsequent) fetches off the currentNetwork emit —
        // a BehaviorSubject that replays the current value to this subscriber.
        this.subscription.add(
            this.apiService.currentNetwork.pipe(debounceTime(500)).subscribe((network: any) => {
                if (!network?.id) {
                    return;
                }
                this.networkId = network.id;
                this.organizationId = network.networkGroupId;
                this.loadRouterMap();
                this.fetch();
            })
        );
        this.disableZitiSheet();
    }

    ngOnChanges(): void {
        if (!this.initialized) {
            return;
        }
        const prevEndpoint = this.endpointId;
        const prevService = this.serviceId;
        this.applyData();
        // Reopened for a different entity (e.g. drawer stays mounted) → refetch.
        if (this.endpointId !== prevEndpoint || this.serviceId !== prevService) {
            this.resetPaging();
            this.fetch();
        }
    }

    ngOnDestroy(): void {
        if (this.autoRefreshHandle) clearInterval(this.autoRefreshHandle);
        this.fetchSub?.unsubscribe();
        this.chartAggSub?.unsubscribe();
        this.loadMoreSub?.unsubscribe();
        this.removeBrushListeners();
        this.detachChartResizeListeners();
        this.restoreZitiSheet();
        this.subscription.unsubscribe();
    }

    private applyData(applyRange = false): void {
        const data = this.data || {};
        // Pre-select the opener's time range on first open only — don't override a user's later
        // manual change (or the range while paging through siblings).
        if (applyRange && this.auditRanges.some((r) => r.id === data.timeRange)) {
            this.selectedRange = data.timeRange as RangeOption['id'];
        }
        if (data.networkId) this.networkId = data.networkId;
        if (data.networkGroupId) this.organizationId = data.networkGroupId;
        if (data.resourceType === 'endpoint') {
            this.isEndpointView = true;
            this.endpointName = data.model?.name ?? '';
            this.endpointId = data.model?.id ?? '';
            this.serviceName = '';
            this.serviceId = '';
        } else if (data.resourceType) {
            this.isEndpointView = false;
            this.serviceName = data.model?.name ?? '';
            this.serviceId = data.model?.id ?? '';
            this.endpointName = '';
            this.endpointId = '';
        }
    }

    // ---- Data fetch (reuses the legacy template-search path) ----------------

    /**
     * Fetch dial records for the active direction. In the endpoint (connector)
     * view a toggle switches between the connector's DIALED side (`identity_id`,
     * server-filtered) and its HOSTED side (`host_id`). The template has no
     * host-side filter, so the hosted mode pulls a broad slice and keeps the
     * records this connector hosts. The service view isn't directional — it
     * server-filters by `serviceId` and captures every dial to the service.
     */
    private fetch(): void {
        if (!this.initialized || !this.networkId || this.organizationId == null) {
            return;
        }
        this.isLoading = true;

        const size = DialLogsTimelineTabComponent.FETCH_SIZE;
        const records$: Observable<DialLogRecord[]> =
            this.isEndpointView && this.directionFilter === 'hosted'
                ? this.queryRecords('', '', size).pipe(map((broad) => broad.filter((r) => r.hostId === this.endpointId)))
                : this.queryRecords(this.endpointId, this.serviceId, size);

        // A fresh window supersedes any in-flight "load older" paging.
        this.loadMoreSub?.unsubscribe();
        this.loadingMore = false;

        // Full-history chart runs in parallel with the (capped) table fetch and replaces its chart.
        this.chartFromAgg = false;
        this.fetchChartBuckets();

        // Cancel any in-flight fetch (e.g. an auto-refresh tick supersedes it).
        this.fetchSub?.unsubscribe();
        this.fetchSub = records$.subscribe({
            next: (records) => {
                // Re-index so the child-row track key stays unique after filtering.
                records.forEach((r, i) => (r.itemIndex = i));
                this.records = records;
                // When the fetch hit the row cap the table only lists dials back to its OLDEST row;
                // remember that timestamp so the chart can mark where the table's coverage ends.
                const cap = Number(DialLogsTimelineTabComponent.FETCH_SIZE);
                this.tableCutoffTs =
                    records.length >= cap
                        ? records.reduce((m, r) => (r.timestampMs < m ? r.timestampMs : m), Number.POSITIVE_INFINITY)
                        : null;
                this.isLoading = false;
                this.errorMessage = undefined;
                this.rebuild();
            },
            error: (error: any) => {
                this.isLoading = false;
                this.errorMessage = 'Error loading dial logs';
                this.logger.error(error);
            },
        });
    }

    /** Run one template-search and map the hits to typed records. `endOverride` pages older by
     * capping the window end (the server returns the newest rows up to it), for "load older". */
    private queryRecords(endpointId: string, serviceId: string, size?: string, endOverride?: moment.Moment): Observable<DialLogRecord[]> {
        const params = this.templateService.getFabricCircuitTemplateQuery(
            'ncfabriccircuits',
            this.newStartTime,
            endOverride ?? this.newEndTime,
            endpointId,
            serviceId,
            this.networkId,
            this.organizationId,
            '',
            '',
            ''
        );
        if (size) {
            params.size = size;
        }
        return this.elasticsearch
            .apiTemplateSearch(this.templateService.SERVICE_ENDPOINT_DIAL_LOGS_TEMPLATE, params)
            .pipe(
                map((data: any) =>
                    toDialLogRecords(this.elasticsearch.hitsToArray(data, 'dial-logs-timeline'), this.eventFields as unknown as DialLogEventFields)
                )
            );
    }

    // ---- Historical chart (server aggregation, bypasses the 10k row cap) -----

    /**
     * The table fetch is capped at FETCH_SIZE (ES max_result_window), so on busy
     * services the chart built from those rows only covers the newest slice of the
     * range. This runs a date_histogram aggregation over the SAME query instead —
     * aggregations process every matching doc, not just the first 10k — and re-renders
     * the chart from full-history bucket counts. It's fire-and-forget: if it fails or
     * the network isn't ready the chart keeps the (capped) client-side render.
     */
    private fetchChartBuckets(): void {
        if (!this.networkId || this.organizationId == null) return;
        const startMs = this.newStartTime.valueOf();
        const endMs = this.newEndTime.valueOf();
        const count = this.bucketCountForRange();
        const body = this.buildDialHistogramQuery(startMs, endMs, count);

        this.chartAggSub?.unsubscribe();
        this.chartAggSub = this.elasticsearch.search(this.organizationId, 'ncfabriccircuits', body).subscribe({
            next: (res: any) => {
                const buckets = this.mapHistogram(res, startMs, endMs, count);
                if (buckets.length) {
                    this.chartFromAgg = true;
                    this.renderChart(buckets);
                }
            },
            error: (err: any) => this.logger.error('Dial-logs chart aggregation failed', err),
        });
    }

    /** date_histogram over ncfabriccircuits with a success/failed split per bucket. Scoping mirrors
     * the table fetch (network + direction); the org is scoped by the request path, so it isn't a
     * query clause. The reporting gateway rejects a query without a bool.must node, so all clauses
     * go in must. The histogram spans the full range via extended_bounds. */
    private buildDialHistogramQuery(startMs: number, endMs: number, count: number): any {
        const bucketMs = Math.max(1, Math.floor((endMs - startMs) / count));
        const must: any[] = [
            { match_phrase: { network_id: this.networkId } },
            { range: { '@timestamp': { gte: startMs, lte: endMs, format: 'epoch_millis' } } },
        ];
        if (this.isEndpointView && this.directionFilter === 'hosted') {
            // Hosted side filters server-side here (the table's template can't), so no client trim needed.
            must.push({ match_phrase: { host_id: this.endpointId } });
        } else {
            if (this.endpointId) must.push({ match_phrase: { identity_id: this.endpointId } });
            if (this.serviceId) must.push({ match_phrase: { service_id: this.serviceId } });
        }
        return {
            size: 0,
            query: { bool: { must } },
            aggs: {
                time_buckets: {
                    date_histogram: {
                        field: '@timestamp',
                        fixed_interval: `${bucketMs}ms`,
                        offset: `${startMs % bucketMs}ms`,
                        min_doc_count: 0,
                        extended_bounds: { min: startMs, max: endMs },
                    },
                    aggs: {
                        outcome: {
                            filters: {
                                filters: {
                                    success: { terms: { event_type: ['created', 'pathUpdated'] } },
                                    failed: { term: { event_type: 'failed' } },
                                },
                            },
                        },
                    },
                },
            },
        };
    }

    /** Map ES date_histogram buckets to the chart's DialTimeBucket shape. */
    private mapHistogram(res: any, startMs: number, endMs: number, count: number): DialTimeBucket[] {
        const raw: any[] = res?.aggregations?.time_buckets?.buckets ?? [];
        const bucketMs = Math.max(1, Math.floor((endMs - startMs) / count));
        return raw.map((b) => {
            const success = b.outcome?.buckets?.success?.doc_count ?? 0;
            const failed = b.outcome?.buckets?.failed?.doc_count ?? 0;
            return { startMs: b.key, endMs: b.key + bucketMs, success, failed, total: b.doc_count ?? 0 };
        });
    }

    refresh(): void {
        this.fetch();
    }

    // ---- Auto-refresh -------------------------------------------------------

    get autoRefreshLabel(): string {
        return this.autoRefreshOptions.find((o) => o.id === this.autoRefreshSeconds)?.label ?? 'Off';
    }

    toggleAutoRefreshMenu(event: MouseEvent): void {
        event.stopPropagation();
        this.autoRefreshMenuOpen = !this.autoRefreshMenuOpen;
    }

    setAutoRefresh(seconds: number): void {
        this.autoRefreshSeconds = seconds;
        this.autoRefreshMenuOpen = false;
        this.restartAutoRefresh();
    }

    private restartAutoRefresh(): void {
        if (this.autoRefreshHandle) {
            clearInterval(this.autoRefreshHandle);
            this.autoRefreshHandle = undefined;
        }
        if (this.autoRefreshSeconds > 0) {
            this.autoRefreshHandle = setInterval(() => {
                if (!this.isLoading) this.fetch();
            }, this.autoRefreshSeconds * 1000);
        }
    }

    /** Any document click closes the open menus (their triggers stop propagation). */
    @HostListener('document:click')
    onDocumentClick(): void {
        this.autoRefreshMenuOpen = false;
        this.columnsMenuOpen = false;
        this.dialerMenuOpen = false;
        this.binderMenuOpen = false;
    }

    // ---- Identity filters (Dialing / Binding) ------------------------------

    private distinctIdentities(
        idOf: (r: DialLogRecord) => string | undefined,
        nameOf: (r: DialLogRecord) => string | undefined
    ): { id: string; name: string }[] {
        const byId = new Map<string, string>();
        for (const r of this.records) {
            const id = idOf(r);
            if (id && !byId.has(id)) byId.set(id, displayEntityName(nameOf(r)) ?? id);
        }
        return [...byId.entries()]
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }

    get dialerOptions(): { id: string; name: string }[] {
        return this.distinctIdentities(
            (r) => r.identityId,
            (r) => r.identityName
        );
    }
    get binderOptions(): { id: string; name: string }[] {
        return this.distinctIdentities(
            (r) => r.hostId,
            (r) => r.hostName
        );
    }

    filteredDialerOptions(): { id: string; name: string }[] {
        const q = this.dialerSearch.toLowerCase().trim();
        return q ? this.dialerOptions.filter((o) => o.name.toLowerCase().includes(q)) : this.dialerOptions;
    }
    filteredBinderOptions(): { id: string; name: string }[] {
        const q = this.binderSearch.toLowerCase().trim();
        return q ? this.binderOptions.filter((o) => o.name.toLowerCase().includes(q)) : this.binderOptions;
    }

    toggleDialerMenu(event: MouseEvent): void {
        event.stopPropagation();
        const open = !this.dialerMenuOpen;
        this.closeAllMenus();
        this.dialerMenuOpen = open;
    }
    toggleBinderMenu(event: MouseEvent): void {
        event.stopPropagation();
        const open = !this.binderMenuOpen;
        this.closeAllMenus();
        this.binderMenuOpen = open;
    }
    private closeAllMenus(): void {
        this.autoRefreshMenuOpen = false;
        this.columnsMenuOpen = false;
        this.dialerMenuOpen = false;
        this.binderMenuOpen = false;
    }

    toggleDialerFilter(id: string): void {
        if (this.dialerFilter.has(id)) {
            this.dialerFilter.delete(id);
        } else {
            this.dialerFilter.add(id);
        }
        this.rebuild();
    }
    toggleBinderFilter(id: string): void {
        if (this.binderFilter.has(id)) {
            this.binderFilter.delete(id);
        } else {
            this.binderFilter.add(id);
        }
        this.rebuild();
    }
    clearDialerFilter(): void {
        this.dialerFilter.clear();
        this.rebuild();
    }
    clearBinderFilter(): void {
        this.binderFilter.clear();
        this.rebuild();
    }

    // ---- View-model roll-up --------------------------------------------------

    private rebuild(): void {
        const decorated = this.records.map((r) => decorateRecord(r));
        const filtered = this.filterRecords(decorated);
        this.groups = this.buildGroups(filtered);
        this.autoExpandSingleGroup();
        this.buildChart(filtered);
    }

    // ---- Activity chart ------------------------------------------------------

    /** ~20–28 bars regardless of range: 3-min@1h, hourly@24h, 6-hourly@7d. */
    private bucketCountForRange(): number {
        if (this.customRange) return 28; // brushed windows: a consistent ~28-bar resolution
        switch (this.selectedRange) {
            case '7d':
                return 28;
            case '1h':
                return 20;
            case '24h':
            default:
                return 24;
        }
    }

    /** Immediate chart from the (capped) fetched rows — a placeholder until the full-history
     * aggregation lands (see fetchChartBuckets), after which it steps aside so table-only filters
     * (search / result) reshape the rows without shrinking the chart back to the capped slice. */
    private buildChart(filtered: DialLogRecord[]): void {
        if (this.chartFromAgg) return;
        const count = this.bucketCountForRange();
        this.renderChart(buildTimeBuckets(filtered, this.newStartTime.valueOf(), this.newEndTime.valueOf(), count));
    }

    /** Lay out the two trend lines from a set of time buckets (client-side or server aggregation). */
    private renderChart(rawBuckets: DialTimeBucket[]): void {
        // Header totals reflect the whole range, including the in-progress bucket.
        this.chartTotalDials = rawBuckets.reduce((s, b) => s + b.total, 0);
        this.chartFailedCount = rawBuckets.reduce((s, b) => s + b.failed, 0);

        // Drop a trailing PARTIAL bucket from the trend line: the query only counts data up to the
        // window end, so if that end falls mid-bucket (true for presets ending "now" AND for a brushed
        // window ending mid-interval) the last bucket under-fills and droops to near-zero at the edge.
        // Compare the bucket's full interval end against the queried end, not the wall clock.
        let buckets = rawBuckets;
        if (buckets.length > 2 && buckets[buckets.length - 1].endMs > this.newEndTime.valueOf()) {
            buckets = buckets.slice(0, -1);
        }
        this.chartBuckets = buckets;
        const count = Math.max(1, buckets.length);
        const success = buckets.map((b) => b.success);
        const failed = buckets.map((b) => b.failed);
        // Shared Y-scale across both series; a hidden series is excluded so isolating one re-zooms.
        // Round up to a clean axis bound so the labels read 1000/500 rather than 923/462.
        const rawMax = Math.max(this.showSuccess ? Math.max(0, ...success) : 0, this.showFailed ? Math.max(0, ...failed) : 0);
        const max = niceMax(rawMax);
        this.chartMax = max;

        const w = this.CHART_VIEW_W;
        const h = this.CHART_VIEW_H;
        this.chartSuccessLine = this.showSuccess ? buildLinePath(success, w, h, max) : '';
        this.chartSuccessArea = buildAreaPath(this.chartSuccessLine, w, h);
        this.chartFailedLine = this.showFailed ? buildLinePath(failed, w, h, max) : '';
        this.chartFailedArea = buildAreaPath(this.chartFailedLine, w, h);

        const denom = Math.max(1, count - 1);
        const scale = max > 0 ? (h - h * 0.08) / max : 0;
        this.chartPoints = buckets.map((b, i) => ({
            xPct: (i / denom) * 100,
            widthPct: 100 / count,
            successYPct: max > 0 ? ((h - b.success * scale) / h) * 100 : 100,
            failedYPct: max > 0 ? ((h - b.failed * scale) / h) * 100 : 100,
            windowLabel: this.bucketWindowLabel(b),
            success: b.success,
            failed: b.failed,
            total: b.total,
        }));
        this.chartXTicks = this.buildXTicks(buckets, count);
        if (this.hoveredBucket != null && this.hoveredBucket >= count) this.hoveredBucket = null;
    }

    /** Rounded mid value for the Y axis. */
    get chartMidValue(): number {
        return Math.round(this.chartMax / 2);
    }

    /** "N failed" share of the charted dials, 0–100. */
    get chartFailedPct(): number {
        return this.chartTotalDials > 0 ? Math.round((this.chartFailedCount / this.chartTotalDials) * 100) : 0;
    }

    /** Failure label — avoids the contradictory "0% failed" when a few failures round down. */
    get chartFailedLabel(): string {
        const pct = this.chartFailedPct;
        return pct === 0 ? '<1% failed' : `${pct}% failed`;
    }

    /** Dial volume shown beside the chart title: "2,113 dials". */
    get chartDialsLabel(): string {
        return `${this.chartTotalDials.toLocaleString()} ${this.chartTotalDials === 1 ? 'dial' : 'dials'}`;
    }

    /** Four evenly-spaced time labels along the X axis. Format scales with the window: minutes for a
     *  sub-2h span (1h preset / short brush), hours up to ~1.5 days, else calendar days. */
    private buildXTicks(buckets: DialTimeBucket[], count: number): string[] {
        if (!count) return [];
        const span = this.chartSpanMs;
        const fmt = span <= 2 * 60 * 60 * 1000 ? 'h:mm A' : span <= 36 * 60 * 60 * 1000 ? 'h A' : 'MMM D';
        const idxs = [0, Math.round((count - 1) / 3), Math.round((2 * (count - 1)) / 3), count - 1];
        return idxs.map((i) => moment(buckets[Math.min(i, count - 1)].startMs).format(fmt));
    }

    /** A series is dimmed when the Result filter excludes it (its data is all zero). */
    get successDimmed(): boolean {
        return this.statusFilter === 'failed';
    }
    get failedDimmed(): boolean {
        return this.statusFilter === 'success';
    }

    toggleSeries(series: 'success' | 'failed'): void {
        if (series === 'success') {
            // Don't allow hiding both.
            if (this.showSuccess && !this.showFailed) return;
            this.showSuccess = !this.showSuccess;
        } else {
            if (this.showFailed && !this.showSuccess) return;
            this.showFailed = !this.showFailed;
        }
        // Re-render from whatever is on screen (agg or client buckets); renderChart honors the flags.
        this.renderChart(this.chartBuckets);
    }

    setHoveredBucket(i: number | null): void {
        if (this.brushing) return; // a live drag owns the pointer — don't flip the hover popover
        this.hoveredBucket = i;
    }

    get hoveredPoint(): DialChartPoint | null {
        return this.hoveredBucket != null ? this.chartPoints[this.hoveredBucket] ?? null : null;
    }

    private bucketWindowLabel(b: DialTimeBucket): string {
        const span = this.chartSpanMs;
        if (span <= 2 * 60 * 60 * 1000) return this.formatBucketMinutes(b);
        if (span > 3 * 24 * 60 * 60 * 1000) return this.formatBucketDay(b);
        return this.formatBucketHours(b);
    }

    // ---- Drag-to-select a custom range (brush) ------------------------------

    /** Current charted span in ms — drives custom-range bucket sizing and label formatting. */
    get chartSpanMs(): number {
        return this.newEndTime.valueOf() - this.newStartTime.valueOf();
    }

    /** X-position (% of the plot) where the table's coverage begins — the oldest dial it lists.
     * Null unless the table is row-capped and that cutoff falls within the charted span. Dials LEFT
     * of it are on the chart only (the table can't reach that far back). */
    get tableCutoffXPct(): number | null {
        const oldest = this.tableCutoffTs;
        const b = this.chartBuckets;
        if (oldest == null || b.length < 2) return null;
        const first = b[0].startMs;
        const last = b[b.length - 1].startMs;
        if (last <= first) return null;
        const frac = (oldest - first) / (last - first);
        return frac > 0.02 && frac < 0.985 ? frac * 100 : null;
    }

    /** Explanation shown on the cutoff marker. */
    get tableCutoffTooltip(): string {
        return `Table shows the ${this.records.length.toLocaleString()} most recent dials. Earlier activity appears on the chart only.`;
    }

    /** True while older dials remain unloaded (the table is row-capped within the range). */
    get hasMoreDials(): boolean {
        return this.tableCutoffTs != null;
    }

    /** Button label: a full next page, or the exact remainder when fewer than a page are left. */
    get loadMoreLabel(): string {
        const cap = Number(DialLogsTimelineTabComponent.FETCH_SIZE);
        const remaining = this.chartTotalDials - this.records.length;
        const next = remaining > 0 && remaining < cap ? remaining : cap;
        return `Load next ${next.toLocaleString()} dials`;
    }

    /** Show the coverage footer only when the range holds more dials than a single page. */
    get showCoverage(): boolean {
        return this.chartTotalDials > Number(DialLogsTimelineTabComponent.FETCH_SIZE);
    }

    /** Share of the total currently in the table; held below full while older pages remain. */
    get coveragePct(): number {
        if (this.chartTotalDials <= 0) return 0;
        if (!this.hasMoreDials) return 100;
        return Math.min(96, Math.round((this.records.length / this.chartTotalDials) * 100));
    }

    /** Load the next batch of OLDER dials (before the current oldest row) and append them. */
    loadOlder(): void {
        if (this.tableCutoffTs == null || this.loadingMore || !this.networkId || this.organizationId == null) return;
        this.loadingMore = true;
        const cap = Number(DialLogsTimelineTabComponent.FETCH_SIZE);
        const before = moment(this.tableCutoffTs - 1); // exclude the boundary row we already hold
        const older$: Observable<DialLogRecord[]> =
            this.isEndpointView && this.directionFilter === 'hosted'
                ? this.queryRecords('', '', DialLogsTimelineTabComponent.FETCH_SIZE, before).pipe(
                      map((broad) => broad.filter((r) => r.hostId === this.endpointId))
                  )
                : this.queryRecords(this.endpointId, this.serviceId, DialLogsTimelineTabComponent.FETCH_SIZE, before);
        this.loadMoreSub?.unsubscribe();
        this.loadMoreSub = older$.subscribe({
            next: (older) => {
                const combined = this.records.concat(older);
                combined.forEach((r, i) => (r.itemIndex = i));
                this.records = combined;
                // A full page means older dials may still remain; a short page means we've reached the
                // window start, so the table now covers the whole range (drop the cutoff marker).
                this.tableCutoffTs =
                    older.length >= cap
                        ? older.reduce((m, r) => (r.timestampMs < m ? r.timestampMs : m), Number.POSITIVE_INFINITY)
                        : null;
                this.loadingMore = false;
                this.rebuild();
            },
            error: (err: any) => {
                this.loadingMore = false;
                this.logger.error('Failed to load older dials', err);
            },
        });
    }

    // ---- Chart resize (drag the divider under the chart to scale plot height) ----

    /** Seed the plot height from the persisted value (or the default), clamped to px bounds. */
    private initChartHeight(): void {
        const rootPx = parseFloat(getComputedStyle(this.document.documentElement).fontSize) || 16;
        this.chartMinPx = Math.round(DialLogsTimelineTabComponent.CHART_MIN_REM * rootPx);
        this.chartMaxPx = Math.round(DialLogsTimelineTabComponent.CHART_MAX_REM * rootPx);
        let px = Math.round(DialLogsTimelineTabComponent.CHART_DEFAULT_REM * rootPx);
        try {
            const saved = Number(localStorage.getItem(DialLogsTimelineTabComponent.CHART_H_KEY));
            if (saved >= this.chartMinPx && saved <= this.chartMaxPx) px = saved;
        } catch {
            // localStorage may be unavailable; fall back to the default height.
        }
        this.chartBodyHeightPx = px;
    }

    startChartResize(event: MouseEvent): void {
        event.preventDefault();
        this.resizingChart = true;
        this.chartResizeStartY = event.clientY;
        this.chartResizeStartH = this.chartBodyHeightPx;
        this.document.addEventListener('mousemove', this.chartMoveHandler);
        this.document.addEventListener('mouseup', this.chartUpHandler);
        // Lock the cursor and suppress selection while dragging.
        this.renderer.setStyle(this.document.body, 'cursor', 'row-resize');
        this.renderer.setStyle(this.document.body, 'user-select', 'none');
    }

    private onChartResizeMove(event: MouseEvent): void {
        if (!this.resizingChart) return;
        // Dragging DOWN (larger clientY) grows the chart.
        const next = this.chartResizeStartH + (event.clientY - this.chartResizeStartY);
        this.chartBodyHeightPx = Math.min(this.chartMaxPx, Math.max(this.chartMinPx, Math.round(next)));
    }

    private endChartResize(): void {
        if (!this.resizingChart) return;
        this.resizingChart = false;
        this.detachChartResizeListeners();
        this.renderer.removeStyle(this.document.body, 'cursor');
        this.renderer.removeStyle(this.document.body, 'user-select');
        try {
            localStorage.setItem(DialLogsTimelineTabComponent.CHART_H_KEY, String(this.chartBodyHeightPx));
        } catch {
            // Persistence is best-effort.
        }
    }

    private detachChartResizeListeners(): void {
        this.document.removeEventListener('mousemove', this.chartMoveHandler);
        this.document.removeEventListener('mouseup', this.chartUpHandler);
    }

    /** Human label for the charted window: the preset name, or a brushed span's actual dates. */
    get rangeLabel(): string {
        if (this.customRange) {
            const s = this.newStartTime;
            const e = this.newEndTime;
            return s.isSame(e, 'day')
                ? `${s.format('MMM D, h:mm A')} – ${e.format('h:mm A')}`
                : `${s.format('MMM D, h:mm A')} – ${e.format('MMM D, h:mm A')}`;
        }
        return this.auditRanges.find((r) => r.id === this.selectedRange)?.rangeLabel ?? 'Last 24 hours';
    }

    /** Reset control: leave the custom window and return to the preset that was active before it. */
    resetRange(): void {
        if (!this.customRange) return;
        this.setRange(this.selectedRange);
    }

    /** Custom tab: open/close the calendar picker (seeded from the current window). */
    toggleCustom(): void {
        this.showCustom = !this.showCustom;
    }

    /** Commit a range picked in the calendar popover as the custom window (refetches). */
    onCustomApply(range: { start: number; end: number }): void {
        this.showCustom = false;
        this.applyBrushedRange(range.start, range.end);
    }

    /** Press on the plot: begin a brush. Window listeners track the drag (outside the zone). */
    onBrushStart(ev: MouseEvent, plot: HTMLElement): void {
        if (ev.button !== 0 || this.isLoading || this.chartMax <= 0) return;
        ev.preventDefault();
        this.brushRect = plot.getBoundingClientRect();
        this.brushStartFrac = this.fracFromEvent(ev, this.brushRect);
        this.brushing = true;
        this.hoveredBucket = null;
        this.updateBrush(ev);
        this.zone.runOutsideAngular(() => {
            window.addEventListener('mousemove', this.onBrushMove);
            window.addEventListener('mouseup', this.onBrushUp);
        });
    }

    @HostListener('document:keydown.escape')
    onBrushEscape(): void {
        this.showCustom = false;
        if (!this.brushing) return;
        this.brushing = false;
        this.removeBrushListeners();
        this.clearBrush();
    }

    private updateBrush(ev: MouseEvent): void {
        if (!this.brushRect) return;
        const cur = this.fracFromEvent(ev, this.brushRect);
        const a = Math.min(this.brushStartFrac, cur);
        const b = Math.max(this.brushStartFrac, cur);
        this.brushLeftPct = a * 100;
        this.brushWidthPct = Math.max(0, (b - a) * 100);
        this.brushTipLeftPct = ((a + b) / 2) * 100;
        this.brushLabel = `${this.fmtBrushBound(this.timeAtFrac(a))} – ${this.fmtBrushBound(this.timeAtFrac(b))}`;
    }

    private endBrush(ev: MouseEvent): void {
        this.removeBrushListeners();
        const rect = this.brushRect;
        this.brushing = false;
        if (!rect) {
            this.clearBrush();
            return;
        }
        // Decide from the real down->up travel, not the band width (a fast drag may fire no moves).
        const cur = this.fracFromEvent(ev, rect);
        if (Math.abs(cur - this.brushStartFrac) * rect.width >= DialLogsTimelineTabComponent.BRUSH_MIN_PX) {
            const a = this.timeAtFrac(Math.min(this.brushStartFrac, cur));
            const b = this.timeAtFrac(Math.max(this.brushStartFrac, cur));
            if (b - a >= DialLogsTimelineTabComponent.BRUSH_MIN_MS) {
                this.applyBrushedRange(a, b);
            }
        }
        this.clearBrush();
    }

    private removeBrushListeners(): void {
        window.removeEventListener('mousemove', this.onBrushMove);
        window.removeEventListener('mouseup', this.onBrushUp);
    }

    private clearBrush(): void {
        this.brushWidthPct = 0;
        this.brushLabel = '';
        this.brushRect = undefined;
    }

    /** clientX -> fraction across the plot (0..1). */
    private fracFromEvent(ev: MouseEvent, rect: DOMRect): number {
        return Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
    }

    /** Fraction across the plot -> timestamp. Buckets are evenly spaced and span the whole window,
     * so a linear map matches what's drawn under the cursor. */
    private timeAtFrac(f: number): number {
        const start = this.newStartTime.valueOf();
        return Math.round(start + f * (this.newEndTime.valueOf() - start));
    }

    private fmtBrushBound(ts: number): string {
        const m = moment(ts);
        return this.chartSpanMs > 24 * 60 * 60 * 1000 ? m.format('MMM D, h:mm A') : m.format('h:mm A');
    }

    /** Apply a brushed sub-range as a custom window and refetch (table + full-history chart). */
    private applyBrushedRange(startMs: number, endMs: number): void {
        this.customRange = true;
        this.newStartTime = moment(startMs);
        this.newEndTime = moment(endMs);
        this.resetPaging();
        this.fetch();
    }

    private formatBucketHours(b: DialTimeBucket): string {
        const start = moment(b.startMs);
        const end = moment(b.endMs);
        // "Aug 21, 2–3 PM" (same day) or spell both ends across a day boundary.
        if (start.isSame(end, 'day')) {
            return `${start.format('MMM D, h')}–${end.format('h A')}`;
        }
        return `${start.format('MMM D, h A')} – ${end.format('MMM D, h A')}`;
    }

    private formatBucketMinutes(b: DialTimeBucket): string {
        // "10:15–10:18 AM" — minute precision for a sub-2h (e.g. 1h) window.
        return `${moment(b.startMs).format('h:mm')}–${moment(b.endMs).format('h:mm A')}`;
    }

    private formatBucketDay(b: DialTimeBucket): string {
        return moment(b.startMs).format('MMM D');
    }

    /**
     * When the assembled result has exactly one group, expand it automatically.
     * Tracks the auto-expanded key so a distinct single-group result expands only
     * once — a manual collapse then sticks across search/filter/auto-refresh rebuilds.
     */
    private autoExpandSingleGroup(): void {
        if (this.groups.length === 1) {
            const key = this.groups[0].key;
            if (this.autoExpandedKey !== key) {
                this.expandedKeys.add(key);
                this.autoExpandedKey = key;
            }
        } else {
            this.autoExpandedKey = undefined;
        }
    }

    private filterRecords(records: DialLogRecord[]): DialLogRecord[] {
        const q = this.search.toLowerCase().trim();
        return records.filter((r) => {
            if (this.statusFilter === 'failed' && r.success !== false) return false;
            if (this.statusFilter === 'success' && r.success !== true) return false;
            if (this.dialerFilter.size && !this.dialerFilter.has(r.identityId ?? '')) return false;
            if (this.binderFilter.size && !this.binderFilter.has(r.hostId ?? '')) return false;
            if (!q) return true;
            const haystack = [
                r.serviceName,
                r.identityName,
                r.hostName,
                r.terminatorRemote,
                r.url,
                r.port != null ? String(r.port) : '',
                r.failureCause,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return haystack.includes(q);
        });
    }

    private buildGroups(records: DialLogRecord[]): DialLogGroup[] {
        const map = new Map<string, DialLogGroup>();
        for (const r of records) {
            const key = dialGroupKey(r, this.isEndpointView);
            let g = map.get(key);
            if (!g) {
                g = {
                    key,
                    primaryName: dialGroupLabel(r, this.isEndpointView),
                    primaryId: (this.isEndpointView ? r.serviceId : r.identityId) ?? null,
                    listenAddress: r.terminatorRemote ?? null,
                    serviceId: r.serviceId ?? null,
                    serviceName: r.serviceName ?? null,
                    // Seam: the client-dialed/intercept address isn't in the dial-log event today.
                    // Populate from a backend-provided field on DialLogRecord once it's added to the template.
                    interceptAddress: null,
                    dialerLabel: null,
                    hostLabel: null,
                    records: [],
                    count: 0,
                    successCount: 0,
                    failedCount: 0,
                    latestTimestampMs: 0,
                };
                map.set(key, g);
            }
            g.records.push(r);
            g.count++;
            if (r.success === true) g.successCount++;
            if (r.success === false) g.failedCount++;
            if (r.timestampMs > g.latestTimestampMs) g.latestTimestampMs = r.timestampMs;
            if (!g.listenAddress && r.terminatorRemote) g.listenAddress = r.terminatorRemote;
        }
        const groups = Array.from(map.values());
        for (const g of groups) {
            g.records.sort((a, b) => b.timestampMs - a.timestampMs);
            g.dialerLabel = collapseNames(g.records.map((r) => displayEntityName(r.identityName)));
            g.hostLabel = collapseNames(g.records.map((r) => displayEntityName(r.hostName)));
        }
        return groups.sort(this.groupComparator());
    }

    private groupComparator(): (a: DialLogGroup, b: DialLogGroup) => number {
        const col = this.groupSortCol;
        const sign = this.groupSortDir === 'asc' ? 1 : -1;
        return (a, b) => {
            let cmp = 0;
            switch (col) {
                case 'name':
                    cmp = (a.primaryName ?? '').localeCompare(b.primaryName ?? '');
                    break;
                case 'listen':
                    cmp = (a.interceptAddress ?? a.listenAddress ?? '').localeCompare(b.interceptAddress ?? b.listenAddress ?? '');
                    break;
                case 'status':
                    cmp = a.failedCount / Math.max(a.count, 1) - b.failedCount / Math.max(b.count, 1);
                    if (cmp === 0) cmp = a.count - b.count;
                    break;
                case 'lastDial':
                    cmp = a.latestTimestampMs - b.latestTimestampMs;
                    break;
            }
            return cmp * sign;
        };
    }

    // ---- Filter / sort / paging controls ------------------------------------

    setRange(id: RangeOption['id']): void {
        // Fall back to 24h (the default), not the first list item, for an unknown/removed id.
        const range =
            this.auditRanges.find((r) => r.id === id) ?? this.auditRanges.find((r) => r.id === '24h') ?? this.auditRanges[0];
        this.selectedRange = range.id;
        this.customRange = false; // picking a preset exits a brushed custom window
        this.newEndTime = moment();
        this.newStartTime = moment().subtract(range.amount, range.unit);
        this.resetPaging();
        this.fetch();
    }

    setStatusFilter(id: StatusOption['id']): void {
        this.statusFilter = id;
        this.resetPaging();
        this.rebuild();
    }

    /** Direction changes WHAT we fetch (dialer vs host side), so it re-queries. */
    setDirection(id: DirectionOption['id']): void {
        if (this.directionFilter === id) return;
        this.directionFilter = id;
        this.resetPaging();
        this.fetch();
    }

    onSearchChange(value: string): void {
        this.search = value;
        this.resetPaging();
        this.rebuild();
    }

    setGroupSort(col: GroupSortColumn): void {
        if (this.groupSortCol === col) {
            this.groupSortDir = this.groupSortDir === 'asc' ? 'desc' : 'asc';
        } else {
            this.groupSortCol = col;
            this.groupSortDir = col === 'lastDial' || col === 'status' ? 'desc' : 'asc';
        }
        this.groups = [...this.groups].sort(this.groupComparator());
    }

    private resetPaging(): void {
        this.groupLimit = this.GROUP_PAGE_SIZE;
        this.childLimits = {};
    }

    // ---- Expand / paging accessors (used by template) -----------------------

    isExpanded(key: string): boolean {
        return this.expandedKeys.has(key);
    }

    toggleGroup(key: string): void {
        if (this.expandedKeys.has(key)) this.expandedKeys.delete(key);
        else this.expandedKeys.add(key);
    }

    get visibleGroups(): DialLogGroup[] {
        return this.groups.slice(0, this.groupLimit);
    }

    childLimit(key: string): number {
        return this.childLimits[key] ?? this.CHILD_PAGE_SIZE;
    }

    visibleChildren(g: DialLogGroup): DialLogRecord[] {
        return g.records.slice(0, this.childLimit(g.key));
    }

    showMoreGroups(): void {
        this.groupLimit += this.GROUP_PAGE_SIZE;
    }

    showMoreChildren(key: string): void {
        this.childLimits = { ...this.childLimits, [key]: this.childLimit(key) + this.CHILD_PAGE_SIZE };
    }

    // ---- Display helpers -----------------------------------------------------

    get primaryColumnLabel(): string {
        // Service view groups by the dialing identity; endpoint view by service.
        return this.isEndpointView ? 'Service' : 'Dialing Identity';
    }

    /** In the hosted direction the connector IS the host, so the varying counterpart is the dialer. */
    get counterpartIsDialer(): boolean {
        return this.isEndpointView && this.directionFilter === 'hosted';
    }

    get counterpartLabel(): string {
        // Service view: the counterpart is the identity binding/hosting the service.
        if (!this.isEndpointView) return 'Binding Identity';
        return this.counterpartIsDialer ? 'Dialing Identity' : 'Dialed Identity';
    }

    counterpartGroupLabel(g: DialLogGroup): string | null {
        return this.counterpartIsDialer ? g.dialerLabel : g.hostLabel;
    }

    // ---- Entity detail links (records carry ziti ids that match the routes) --

    private identityLink(id: string | null | undefined): any[] | null {
        return id ? ['/identities', id] : null;
    }

    private serviceLink(id: string | null | undefined): any[] | null {
        return id ? ['/services/advanced', id, 'overview'] : null;
    }

    /** Route for the group's primary name — service (identity view) or identity (service view). */
    primaryLink(g: DialLogGroup): any[] | null {
        return this.isEndpointView ? this.serviceLink(g.primaryId) : this.identityLink(g.primaryId);
    }

    /** Route for the group's counterpart identity — only when the group maps to a single identity (not "Multiple"). */
    counterpartGroupLink(g: DialLogGroup): any[] | null {
        const ids = new Set(
            g.records.map((r) => (this.counterpartIsDialer ? r.identityId : r.hostId)).filter((id): id is string => !!id)
        );
        return ids.size === 1 ? this.identityLink([...ids][0]) : null;
    }

    /** A group whose counterpart varies ("Multiple") expands into one sub-group per
     *  binding identity, each itself expandable to just that binder's dials. */
    showBinderGroups(g: DialLogGroup): boolean {
        return this.counterpartGroupLabel(g) === 'Multiple';
    }

    /** Sub-group a group's dials by the varying counterpart (binding identity), most-dialed first. */
    binderSubgroups(g: DialLogGroup): BinderSubgroup[] {
        const byId = new Map<string, BinderSubgroup>();
        for (const r of g.records) {
            const id = (this.counterpartIsDialer ? r.identityId : r.hostId) ?? '';
            let sub = byId.get(id);
            if (!sub) {
                sub = {
                    id: id || '—',
                    name: displayEntityName(this.counterpartIsDialer ? r.identityName : r.hostName),
                    link: id ? this.identityLink(id) : null,
                    records: [],
                    count: 0,
                    failedCount: 0,
                    latestTimestampMs: 0,
                };
                byId.set(id, sub);
            }
            sub.records.push(r);
            sub.count++;
            if (r.success === false) sub.failedCount++;
            if (r.timestampMs > sub.latestTimestampMs) sub.latestTimestampMs = r.timestampMs;
        }
        return [...byId.values()].sort((a, b) => b.count - a.count);
    }

    /** Grid template that aligns a binder row's cells with the parent table's
     *  columns. The name spans the identity columns (dialing + binding) and
     *  truncates there; Dialed Address / Dials / Last Dial line up 1:1. */
    get binderGridTemplate(): string {
        const lead = this.colWidths[0] + (this.colVisible['counterpart'] ? this.colWidths[1] : 0);
        const cols = [`${lead}%`];
        if (this.colVisible['addr']) cols.push(`${this.colWidths[2]}%`);
        if (this.colVisible['dials']) cols.push(`${this.colWidths[3]}%`);
        if (this.colVisible['time']) cols.push(`${this.colWidths[4]}%`);
        return cols.join(' ');
    }

    // Binder sub-groups reuse the group expand/pagination state under a composite key.
    binderKey(groupKey: string, binderId: string): string {
        return `${groupKey}::b::${binderId}`;
    }
    isBinderExpanded(groupKey: string, binderId: string): boolean {
        return this.isExpanded(this.binderKey(groupKey, binderId));
    }
    toggleBinder(groupKey: string, binderId: string): void {
        this.toggleGroup(this.binderKey(groupKey, binderId));
    }

    /** Paginated dials for any dial list (a single-binder group or a binder sub-group). */
    visibleChildrenOf(records: DialLogRecord[], key: string): DialLogRecord[] {
        return records.slice(0, this.childLimit(key));
    }

    // Signature rollups: bucket a leaf's dials so hundreds of identical rows collapse to a few
    // (by error code + target for failures, by target for successes), each expandable to the dials.
    private sigCache = new WeakMap<DialLogRecord[], DialSignatureGroup[]>();

    /** Cached per records array (rebuilt into fresh arrays each fetch, so the cache self-invalidates). */
    signatureGroups(records: DialLogRecord[]): DialSignatureGroup[] {
        let cached = this.sigCache.get(records);
        if (!cached) {
            cached = buildSignatureGroups(records);
            this.sigCache.set(records, cached);
        }
        return cached;
    }

    /** Expand/paging key for one signature bucket under a leaf (composite, like binderKey). */
    signatureKey(parentKey: string, sigKey: string): string {
        return `${parentKey}::s::${sigKey}`;
    }

    /** Navigating away from a drawer should dismiss it; harmless in the detail tab. */
    onEntityLinkClick(event: MouseEvent): void {
        event.stopPropagation();
        this.drawerService.close();
    }

    /** Empty-state hint — nudges toward the other direction in the connector view. */
    get emptyStateSubtitle(): string {
        return this.hasMoreDials
            ? `No matches in the ${this.records.length.toLocaleString()} most recent dials loaded — older activity may match.`
            : 'No dial log results returned using the selected criteria.';
    }

    /** Fetch the network's edge routers once, so the failure modal can resolve
     *  the circuit's zitiId path.nodes to router names + detail links. */
    private loadRouterMap(): void {
        if (this.routerMapLoaded || !this.networkId) {
            return;
        }
        this.routerMapLoaded = true;
        this.edgeRouterService
            .getEdgeRouterPage({ params: { networkId: this.networkId }, pageParams: { size: 2000 } } as any)
            .then((routers: any[]) => {
                (routers || []).forEach((r) => {
                    if (r?.zitiId && r?.id) {
                        this.routerNameMap.set(r.zitiId, { name: r.name, id: r.id });
                    }
                });
            })
            .catch((err) => this.logger.error(err));
    }

    /** A failed child row opens the full failure detail modal; other rows are inert. */
    onChildRowClick(r: DialLogRecord): void {
        if (r.success === false) {
            this.openFailureDetail(r);
        }
    }

    /** Full detail of one failed dial — everything we extracted, plus the remediation help text. */
    openFailureDetail(r: DialLogRecord): void {
        this.dialog.open(DialLogsFailureDetailComponent, {
            data: { record: r, routers: this.routerNameMap },
            width: '680px',
            maxWidth: '92vw',
            autoFocus: false,
            panelClass: 'dlt-failure-detail-panel',
        });
    }

    /** Tooltip for the Error Code cell — the decoded headline + remediation hint. */
    failureTooltip(r: DialLogRecord): string {
        if (!r.failureHint) return '';
        const headline = r.failureHint.headline.map((s) => s.text).join('');
        return headline ? `${headline} — ${r.failureHint.explanation}` : r.failureHint.explanation;
    }

    formatServiceFallback(s: string | null | undefined): string {
        return displayEntityName(s) ?? '—';
    }

    // ---- Column visibility (show/hide the parent table's secondary columns) ------

    /** Toggleable parent columns (labels are view-aware). Primary is always shown. */
    get columnOptions(): { key: string; label: string; visible: boolean }[] {
        return [
            { key: 'counterpart', label: this.counterpartLabel, visible: this.colVisible['counterpart'] },
            { key: 'addr', label: 'Dialed Address', visible: this.colVisible['addr'] },
            { key: 'dials', label: 'Dials', visible: this.colVisible['dials'] },
            { key: 'time', label: 'Last Dial', visible: this.colVisible['time'] },
        ];
    }

    /** Toggleable expanded-detail columns. The Event column is always shown. */
    get detailColumnOptions(): { key: string; label: string; visible: boolean }[] {
        return [
            { key: 'target', label: 'Target Address', visible: this.detailColVisible['target'] },
            { key: 'error', label: 'Error Code', visible: this.detailColVisible['error'] },
            { key: 'time', label: 'Time', visible: this.detailColVisible['time'] },
        ];
    }

    get hiddenColumnCount(): number {
        const hidden = (o: Record<string, boolean>) => Object.values(o).filter((v) => !v).length;
        return hidden(this.colVisible) + hidden(this.detailColVisible);
    }

    /** Parent columns currently rendered (primary + visible secondaries) — the detail row's colspan. */
    get visibleColCount(): number {
        return 1 + Object.values(this.colVisible).filter((v) => v).length;
    }

    /** Detail columns currently rendered (Event + always-on Dials + visible secondaries) — the detail table's colspans. */
    get visibleDetailColCount(): number {
        return 2 + Object.values(this.detailColVisible).filter((v) => v).length;
    }

    toggleColumnsMenu(event: MouseEvent): void {
        event.stopPropagation();
        this.columnsMenuOpen = !this.columnsMenuOpen;
    }

    toggleColumn(key: string): void {
        this.colVisible[key] = !this.colVisible[key];
    }

    toggleDetailColumn(key: string): void {
        this.detailColVisible[key] = !this.detailColVisible[key];
    }

    resetColumns(): void {
        this.colVisible = { ...DialLogsTimelineTabComponent.DEFAULT_COL_VISIBLE };
        this.detailColVisible = { ...DialLogsTimelineTabComponent.DEFAULT_DETAIL_COL_VISIBLE };
    }

    // ---- Column resize (drag a header edge; adjusts that column + its neighbour) --

    startColResize(event: MouseEvent, index: number): void {
        event.preventDefault();
        event.stopPropagation(); // don't trigger the column's sort
        const table = (event.target as HTMLElement).closest('table');
        if (!table) return;
        this.colResizeIndex = index;
        this.colResizeStartX = event.clientX;
        this.colResizeTableWidth = table.getBoundingClientRect().width;
        this.colResizeStartWidth = this.colWidths[index];
        this.colResizePairTotal = this.colWidths[index] + this.colWidths[index + 1];
        this.document.addEventListener('mousemove', this.colResizeMove);
        this.document.addEventListener('mouseup', this.colResizeUp);
        this.renderer.setStyle(this.document.body, 'cursor', 'col-resize');
        this.renderer.setStyle(this.document.body, 'user-select', 'none');
    }

    private onColResizeMove(event: MouseEvent): void {
        if (this.colResizeIndex < 0 || this.colResizeTableWidth <= 0) return;
        const deltaPct = ((event.clientX - this.colResizeStartX) / this.colResizeTableWidth) * 100;
        const min = DialLogsTimelineTabComponent.MIN_COL_PCT;
        // Grow this column and shrink its neighbour, keeping both above the minimum.
        const width = Math.max(min, Math.min(this.colResizePairTotal - min, this.colResizeStartWidth + deltaPct));
        this.colWidths[this.colResizeIndex] = width;
        this.colWidths[this.colResizeIndex + 1] = this.colResizePairTotal - width;
    }

    private endColResize(): void {
        this.colResizeIndex = -1;
        this.document.removeEventListener('mousemove', this.colResizeMove);
        this.document.removeEventListener('mouseup', this.colResizeUp);
        this.renderer.removeStyle(this.document.body, 'cursor');
        this.renderer.removeStyle(this.document.body, 'user-select');
    }

    // ---- Detail (child) column resize --------------------------------------

    /** Ordered, currently-visible detail column keys (drives resize pairing). */
    detailColKeys(): string[] {
        const keys = ['event'];
        if (this.detailColVisible['target']) keys.push('target');
        if (this.detailColVisible['error']) keys.push('error');
        keys.push('dials'); // always shown — the bucket count column
        if (this.detailColVisible['time']) keys.push('time');
        return keys;
    }

    startDetailColResize(event: MouseEvent, key: string): void {
        event.preventDefault();
        event.stopPropagation();
        const table = (event.target as HTMLElement).closest('table');
        if (!table) return;
        const keys = this.detailColKeys();
        const idx = keys.indexOf(key);
        if (idx < 0 || idx >= keys.length - 1) return; // last visible column has no neighbour to shrink
        this.detailResizeKey = key;
        this.detailResizeNextKey = keys[idx + 1];
        this.detailResizeStartX = event.clientX;
        this.detailResizeTableWidth = table.getBoundingClientRect().width;
        this.detailResizeStartWidth = this.detailColWidths[key];
        this.detailResizePairTotal = this.detailColWidths[key] + this.detailColWidths[this.detailResizeNextKey];
        this.document.addEventListener('mousemove', this.detailResizeMove);
        this.document.addEventListener('mouseup', this.detailResizeUp);
        this.renderer.setStyle(this.document.body, 'cursor', 'col-resize');
        this.renderer.setStyle(this.document.body, 'user-select', 'none');
    }

    private onDetailColResizeMove(event: MouseEvent): void {
        if (!this.detailResizeKey || !this.detailResizeNextKey || this.detailResizeTableWidth <= 0) return;
        const deltaPct = ((event.clientX - this.detailResizeStartX) / this.detailResizeTableWidth) * 100;
        const min = DialLogsTimelineTabComponent.MIN_COL_PCT;
        const width = Math.max(min, Math.min(this.detailResizePairTotal - min, this.detailResizeStartWidth + deltaPct));
        this.detailColWidths[this.detailResizeKey] = width;
        this.detailColWidths[this.detailResizeNextKey] = this.detailResizePairTotal - width;
    }

    private endDetailColResize(): void {
        this.detailResizeKey = null;
        this.detailResizeNextKey = null;
        this.document.removeEventListener('mousemove', this.detailResizeMove);
        this.document.removeEventListener('mouseup', this.detailResizeUp);
        this.renderer.removeStyle(this.document.body, 'cursor');
        this.renderer.removeStyle(this.document.body, 'user-select');
    }

    /** Absolute timestamp for tooltips — matches the legacy console format. */
    formatTimestamp(ms: number): string {
        return moment.utc(ms).local().format('M/D/YY h:mm a');
    }

    /** Relative timestamp for row display, backed by moment for consistency. */
    formatTimestampRelative(ms: number): string {
        const deltaSec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
        if (deltaSec < 30) return 'Just now';
        if (deltaSec < 60) return `${deltaSec}s ago`;
        const minutes = Math.floor(deltaSec / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        if (days < 7) return `${days}d ago`;
        return this.formatTimestamp(ms);
    }

    // ---- ziti-console-lib global stylesheet toggle (see legacy dial-logs) ---

    private disableZitiSheet(): void {
        const sheets = Array.from(this.document.styleSheets);
        this.zitiSheet = sheets.find((sheet: CSSStyleSheet) => !!sheet.href && sheet.href.includes('ziti.css'));
        if (this.zitiSheet) {
            this.renderer.setProperty(this.zitiSheet, 'disabled', true);
        }
    }

    private restoreZitiSheet(): void {
        if (this.zitiSheet) {
            this.renderer.setProperty(this.zitiSheet, 'disabled', false);
        }
    }
}
